import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  SimpleGrid,
  Box,
  Text,
  VStack,
  Progress,
  Icon,
  HStack,
  Badge,
  Tooltip,
  Divider,
} from '@chakra-ui/react';
import { FaTrophy, FaLock, FaUnlock, FaStar } from 'react-icons/fa';
import { ACHIEVEMENTS } from '../services/achievementService';
import { getUnlockedAchievements } from '../services/achievementService';
import { motion } from 'framer-motion';

const MotionBox = motion(Box);

const AchievementCard = ({ achievement, isUnlocked }) => {
  return (
    <Tooltip
      label={isUnlocked ? 'Achievement Unlocked!' : 'Keep playing to unlock this achievement'}
      placement="top"
      hasArrow
    >
      <MotionBox
        whileHover={{ scale: 1.02 }}
        transition={{ duration: 0.2 }}
        p={4}
        borderRadius="lg"
        bg={isUnlocked ? 'yellow.50' : 'gray.50'}
        opacity={isUnlocked ? 1 : 0.7}
        border="1px solid"
        borderColor={isUnlocked ? 'yellow.200' : 'gray.200'}
        position="relative"
        overflow="hidden"
        role="group"
        cursor="pointer"
      >
        {/* Achievement Status Badge */}
        <Badge
          position="absolute"
          top={2}
          right={2}
          colorScheme={isUnlocked ? 'green' : 'gray'}
        >
          <HStack spacing={1}>
            <Icon as={isUnlocked ? FaUnlock : FaLock} boxSize={3} />
            <Text>{isUnlocked ? 'Unlocked' : 'Locked'}</Text>
          </HStack>
        </Badge>

        <VStack align="start" spacing={2}>
          <HStack spacing={3} width="100%">
            <Text fontSize="2xl">{achievement.icon}</Text>
            <VStack align="start" spacing={0}>
              <Text fontWeight="bold" fontSize="lg">{achievement.name}</Text>
              <Text fontSize="sm" color="gray.600">
                {achievement.description}
              </Text>
            </VStack>
          </HStack>

          {/* Rarity Indicator */}
          <HStack spacing={1} opacity={0.7}>
            <Icon as={FaStar} color="yellow.500" boxSize={3} />
            <Text fontSize="xs">
              {getAchievementRarity(achievement.id)}% of players
            </Text>
          </HStack>
        </VStack>

        {/* Achievement Glow Effect */}
        {isUnlocked && (
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bgGradient="radial(circle at 50% 50%, yellow.100, transparent)"
            opacity={0}
            _groupHover={{ opacity: 0.5 }}
            transition="opacity 0.2s"
          />
        )}
      </MotionBox>
    </Tooltip>
  );
};

const getAchievementRarity = (achievementId) => {
  // This could be replaced with actual stats from your backend
  const rarityMap = {
    FIRST_WIN: 85,
    PERFECT_ROUND: 45,
    STREAK_3: 35,
    NO_HINTS: 25,
    HIGH_SCORE_500: 15,
    HARD_MODE: 10,
    COMBO_MASTER: 20
  };
  return rarityMap[achievementId] || 0;
};

const AchievementsModal = ({ isOpen, onClose }) => {
  const unlockedAchievements = getUnlockedAchievements();
  const totalAchievements = Object.keys(ACHIEVEMENTS).length;
  const progress = (unlockedAchievements.length / totalAchievements) * 100;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay backdropFilter="blur(10px)" />
      <ModalContent>
        <ModalHeader>
          <HStack>
            <Icon as={FaTrophy} color="yellow.400" boxSize={6} />
            <Text>Achievements</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <VStack spacing={6} align="stretch">
            {/* Progress Section */}
            <Box bg="gray.50" p={4} borderRadius="lg">
              <Text fontWeight="bold" mb={2}>
                Achievement Progress: {unlockedAchievements.length} / {totalAchievements}
              </Text>
              <Tooltip 
                label={`${unlockedAchievements.length} out of ${totalAchievements} achievements unlocked`}
                placement="top"
                hasArrow
              >
                <Box>
                  <Progress 
                    value={progress} 
                    colorScheme="yellow" 
                    borderRadius="full"
                    size="lg"
                    hasStripe
                    isAnimated
                  />
                </Box>
              </Tooltip>
              <Text fontSize="sm" color="gray.600" mt={2}>
                {Math.round(progress)}% Complete
              </Text>
            </Box>

            <Divider />

            {/* Achievements Grid */}
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              {Object.values(ACHIEVEMENTS).map((achievement) => (
                <AchievementCard
                  key={achievement.id}
                  achievement={achievement}
                  isUnlocked={unlockedAchievements.includes(achievement.id)}
                />
              ))}
            </SimpleGrid>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default AchievementsModal; 